
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { Sparkles, Clock, TrendingUp } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { ProductCard } from './ProductCard';
import { ErrorBoundary } from './ErrorBoundary';

async function fetchNewReleases() {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .gte('created_at', thirtyDaysAgo.toISOString())
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) throw new Error(error.message);
  return data || [];
}

export function NewReleasesPage() {
  const { data: products = [], isLoading, error } = useQuery({
    queryKey: ['newReleases'],
    queryFn: fetchNewReleases,
    refetchInterval: 300000, // 5 minutes
  });

  const title = 'New Releases - Latest Tech Products 2025';
  const description = 'Discover the newest tech products and gadgets. Stay ahead with the latest releases in computers, electronics, and tech accessories.';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-gray-200 rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="h-48 bg-gray-100 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg max-w-md">
          <p className="text-red-800">Failed to load new releases: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="/new-releases" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <header className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 mr-3" />
                <h1 className="text-2xl font-semibold text-white">New Releases</h1>
              </div>
              <p className="text-xl opacity-90 max-w-3xl mx-auto">
                Discover the latest tech products and gadgets hitting the market
              </p>
              <div className="mt-4 inline-flex items-center text-sm opacity-75">
                <Clock className="w-4 h-4 mr-2" />
                Updated daily
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 py-8">
          {products.length === 0 ? (
            <div className="text-center py-12">
              <TrendingUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-700 mb-2">No New Releases Found</h2>
              <p className="text-gray-500">Check back later for the latest products.</p>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Sparkles className="w-5 h-5 text-blue-600 mr-2" />
                      <span className="font-medium text-blue-800">
                        {products.length} New Products Found
                      </span>
                    </div>
                    <div className="text-sm text-blue-600">
                      ✨ Latest releases from the past 30 days
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map((product) => (
                  <ErrorBoundary key={product.asin}>
                    <ProductCard product={product} />
                  </ErrorBoundary>
                ))}
              </div>
            </>
          )}
        </main>
      </div>
    </>
  );
}
